/**
 * PHARMAKON — the one team workspace, its pure rules (X XWP-68; CP §15.3 intro + bullets 1-4, §15.20
 * bullet 4; XO-11, XD-225, XMD-CP-20).
 *
 * `/equipe` (canonical) and Paramètres › Utilisateurs render the same `components/equipe/EquipeEspace.jsx`
 * (refonte A-42 entrée pattern). This module holds what both decide the same way: who may do what
 * (the browser mirror of the server matrices, `config/permissions.config.js` — the server stays the
 * authority, a refused call is answered there), which tabs the user sidebar shows, which reader each tab
 * calls, the list order and filters, and the French copy. Nothing here is a business value of its own:
 * the PIN rules, the password rules, the name bounds and the seat amounts are the server's, shown as it
 * answers them (`erreursChamps`, the seat envelope).
 */
import { can } from "@/config/permissions.config.js";
import { LOCALE_LABELS } from "@/config/locale.config.js";
import { ROLES } from "@/config/routeAccess.config.js";
import { fmtDate, fmtTime } from "@/config/locale.config.js";
import { presentationMonSiege } from "@/lib/siege-utilisateur.js";

/** The tabs of the user sidebar, in display order. */
export const ONGLETS_UTILISATEUR = Object.freeze({
  PROFIL: "profil",
  ACTIVITE: "activite",
  VENTES: "ventes",
  SECURITE: "securite",
});

export const LIBELLES_ONGLET = Object.freeze({
  [ONGLETS_UTILISATEUR.PROFIL]: "Profil",
  [ONGLETS_UTILISATEUR.ACTIVITE]: "Activité",
  [ONGLETS_UTILISATEUR.VENTES]: "Ventes",
  [ONGLETS_UTILISATEUR.SECURITE]: "Sécurité",
});

/** The role labels the team screens have always shown (the role CODE `CAISSIER` is unchanged, CP §15.3). */
export const LIBELLES_ROLE = Object.freeze({
  [ROLES.PROPRIETAIRE]: LOCALE_LABELS[ROLES.PROPRIETAIRE],
  [ROLES.MANAGER]: LOCALE_LABELS[ROLES.MANAGER],
  [ROLES.CAISSIER]: "Caissier(ère)",
});

/** The roles the owner may give a new or edited user (a pharmacy has one owner, created at onboarding). */
export function rolesAttribuables() {
  return [ROLES.MANAGER, ROLES.CAISSIER].map((value) => ({ value, label: LIBELLES_ROLE[value] }));
}

/** The browser mirror of the server photo rules (`apps/server/src/config/photo-utilisateur.config.js`, parity-tested). */
export const PHOTO_UTILISATEUR = Object.freeze({
  CHAMP: "photo",
  TYPES: Object.freeze(["image/jpeg", "image/png", "image/webp"]),
  ACCEPT: "image/jpeg,image/png,image/webp",
});

/** The list filters (client-side over the team, which `GET /users` answers whole for an officine). */
export const FILTRE_TOUS = "TOUS";
export const FILTRE_STATUT = Object.freeze({ TOUS: FILTRE_TOUS, ACTIFS: "ACTIFS", INACTIFS: "INACTIFS" });

/** French copy of the workspace — « Utilisateur » for the product term, the role labels kept. */
export const MESSAGES_EQUIPE = Object.freeze({
  titre: "Équipe & utilisateurs",
  sousTitre: (n) => `${n} utilisateur${n > 1 ? "s" : ""} de l'officine`,
  lectureSeule: "Lecture seule : seul le propriétaire ajoute ou modifie les utilisateurs.",
  ajouter: "Ajouter un utilisateur",
  recherche: "Rechercher un utilisateur",
  grille: "Utilisateurs de l'officine",
  filtreRole: "Rôle",
  filtreStatut: "Statut",
  tous: "Tous",
  actifs: "Actifs",
  inactifs: "Inactifs",
  vide: "Aucun utilisateur",
  videAide: "Ajoutez un premier utilisateur pour commencer.",
  videFiltre: "Aucun utilisateur ne correspond à ces filtres.",
  erreur: "La liste de l'équipe n'a pas pu être chargée",
  vous: "(vous)",
  actif: "Actif",
  inactif: "Inactif",
  jamais: "Jamais",
  colonnes: Object.freeze({
    utilisateur: "Utilisateur",
    role: "Rôle",
    email: "E-mail",
    telephone: "Téléphone",
    connexion: "Dernière connexion",
    statut: "Statut",
    siege: "Siège",
  }),
  ouvrir: "Ouvrir la fiche",
  sieges: Object.freeze({
    titre: "Sièges utilisateurs",
    actifs: (n) => `${n} siège${n > 1 ? "s" : ""} actif${n > 1 ? "s" : ""}`,
    enAttente: (n) => `${n} en attente de paiement`,
    tarif: (montant) => `${montant} par utilisateur et par an`,
    fin: (date) => `Période de licence jusqu'au ${date}`,
    proprietaire: "Le propriétaire et le compte d'équipe n'occupent pas de siège.",
  }),
  tiroir: Object.freeze({
    chargement: "Chargement de la fiche…",
    onglets: "Fiche de l'utilisateur",
    modifier: "Modifier",
    precedent: "Utilisateur précédent",
    suivant: "Utilisateur suivant",
    plus: "Plus d'actions",
  }),
  profil: Object.freeze({
    prenom: "Prénom",
    nom: "Nom",
    email: "Adresse e-mail",
    telephone: "Téléphone",
    role: "Rôle",
    statut: "Statut",
    creation: "Créé le",
    connexion: "Dernière connexion",
    siege: "Siège",
    emailFixe: "L'adresse e-mail ne se modifie pas.",
    roleProprietaire: "Le rôle du propriétaire ne se modifie pas.",
  }),
  photo: Object.freeze({
    alt: (nom) => `Photo de ${nom}`,
    choisir: "Choisir une photo",
    changer: "Changer la photo",
    retirer: "Retirer la photo",
    aide: "JPEG, PNG ou WebP. La photo est recadrée en cercle à l'affichage.",
    enregistree: "Photo enregistrée.",
    retiree: "Photo retirée.",
    choisie: (nom) => `Photo choisie : ${nom}`,
    aucune: "Aucune photo choisie",
    echecApresCreation: "L'utilisateur a été créé, mais sa photo n'a pas pu être enregistrée :",
  }),
  creation: Object.freeze({
    titre: "Ajouter un utilisateur",
    sousTitre: "Il se connectera avec son adresse e-mail et ce mot de passe.",
    motDePasse: "Mot de passe initial",
    motDePasseAide: "Au moins 8 caractères, une majuscule et un chiffre.",
    creer: "Créer l'utilisateur",
    cree: (nom) => `Utilisateur ${nom} créé.`,
  }),
  edition: Object.freeze({
    titre: "Modifier l'utilisateur",
    enregistrer: "Enregistrer",
    rienAEnregistrer: "Aucune modification à enregistrer.",
    enregistre: (nom) => `${nom} mis à jour.`,
  }),
  activite: Object.freeze({
    chargement: "Chargement de l'activité…",
    erreur: "L'activité n'a pas pu être chargée",
    vide: "Aucune opération enregistrée pour cet utilisateur.",
    ouvrir: "Ouvrir",
    quand: "Date",
    quoi: "Opération",
    objet: "Objet",
    total: (n) => `${n} opération${n > 1 ? "s" : ""}`,
  }),
  ventes: Object.freeze({
    chargement: (nom) => `Chargement des ventes de ${nom}…`,
    erreur: (nom) => `Les ventes de ${nom} n'ont pas pu être chargées`,
    periodes: (nom) => `Période des ventes de ${nom}`,
    miennes: "Période de mes ventes",
  }),
  securite: Object.freeze({
    pin: "PIN personnel",
    pinAide: "Le PIN sert à choisir l'utilisateur sur le poste d'équipe et à confirmer ses opérations de caisse. Il n'est jamais affiché.",
    definir: "Définir le PIN",
    redefinir: "Redéfinir le PIN",
    retirer: "Retirer le PIN",
    changerMien: "Changer mon PIN",
    nouveauPin: "Nouveau PIN",
    confirmationPin: "Confirmer le PIN",
    pinDifferents: "Les deux PIN saisis ne sont pas identiques.",
    pinRequis: "Saisissez le PIN.",
    pinDefini: (nom) => `PIN de ${nom} enregistré.`,
    pinRetire: (nom) => `PIN de ${nom} retiré.`,
    monPinChange: "Votre PIN est enregistré.",
    motDePasseActuel: "Votre mot de passe actuel",
    motDePasseActuelAide: "Il confirme que c'est bien vous qui changez votre PIN.",
    retirerTitre: (nom) => `Retirer le PIN de ${nom} ?`,
    retirerMessage: "L'utilisateur ne pourra plus être choisi sur le poste d'équipe tant qu'un nouveau PIN n'est pas défini.",
    motDePasse: "Mot de passe",
    reinitialiser: "Réinitialiser le mot de passe",
    nouveauMotDePasse: "Nouveau mot de passe",
    reinitialise: (nom) => `Mot de passe de ${nom} réinitialisé.`,
    acces: "Accès",
    desactiver: "Désactiver l'utilisateur",
    reactiver: "Réactiver l'utilisateur",
    activerPayer: "Activer — régler le siège",
    desactiverTitre: (nom) => `Désactiver ${nom} ?`,
    desactiverMessage: (nom) => `${nom} ne pourra plus se connecter. Vous pourrez le réactiver à tout moment.`,
    reactiverTitre: (nom) => `Réactiver ${nom} ?`,
    reactiverMessage: (nom) => `${nom} pourra à nouveau se connecter.`,
    desactive: "Utilisateur désactivé.",
    reactive: "Utilisateur réactivé.",
    supprimer: "Supprimer le compte",
    supprimerTitre: (nom) => `Supprimer le compte de ${nom} ?`,
    supprimerMessage:
      "Le compte et sa photo sont retirés ; son historique (ventes, journal) reste attribué à son nom. Cette action est définitive.",
    supprime: (nom) => `Compte de ${nom} supprimé.`,
    soi: "Votre propre accès et votre mot de passe se gèrent depuis « Mon compte ».",
  }),
  chargementPin: "Lecture du PIN…",
});

const sansAccents = (s) =>
  String(s ?? "")
    .normalize("NFD")
    .replace(/[̀-ͯ]/g, "")
    .toLowerCase();

/** « Prénom Nom ». */
export function nomComplet(u) {
  return [u?.prenom, u?.nom].filter(Boolean).join(" ").trim();
}

/** Two initials, « ? » when nothing is known. */
export function initiales(u) {
  return `${u?.prenom?.[0] ?? ""}${u?.nom?.[0] ?? ""}`.toUpperCase() || "?";
}

/** Whether the server holds a photo for the user (its public trace, XD-270). */
export const aUnePhoto = (u) => Boolean(u?.photo_maj_le);

/** What the reader may do in the workspace (UI gate; the server refuses the rest). */
export function droitsEquipe(moi) {
  const r = (ressource, action) => Boolean(moi?.role) && can(moi, ressource, action);
  return {
    lire: r("user", "read"),
    creer: r("user", "create"),
    modifier: r("user", "update"),
    supprimer: r("user", "delete"),
    photo: r("user_profil", "photo_gerer"),
    pin: r("user_pin", "manage"),
    sieges: r("siege_utilisateur", "equipe"),
    payerSiege: r("siege_utilisateur", "gerer"),
  };
}

const estSoi = (moi, cible) => Boolean(moi?.id && cible?.id && moi.id === cible.id);

/**
 * The tabs of the user sidebar for `moi` looking at `cible`: Profil always; Activité and Ventes for the
 * PROPRIÉTAIRE or oneself (CP §15.3 bullets 3-4, XD-225 — a MANAGER never reads a colleague's); Sécurité
 * (PIN, password, access) for the PROPRIÉTAIRE only.
 */
export function ongletsUtilisateur(moi, cible) {
  const O = ONGLETS_UTILISATEUR;
  if (!moi?.role || !cible) return [O.PROFIL];
  const soi = estSoi(moi, cible);
  const onglets = [O.PROFIL];
  if (soi ? can(moi, "user_profil", "self") : can(moi, "user_profil", "activite_autres")) onglets.push(O.ACTIVITE);
  if (soi ? can(moi, "ventes_utilisateur", "self") : can(moi, "ventes_utilisateur", "autres")) onglets.push(O.VENTES);
  if (can(moi, "user_pin", "manage")) onglets.push(O.SECURITE);
  return onglets;
}

/** The Ventes reader: one's own through /users/moi/ventes (the owner too — XD-297), another's through /users/:id/ventes. */
export function lecteurVentes(moi, cible, api) {
  return estSoi(moi, cible) ? () => api.mesVentes() : () => api.ventesUtilisateur(cible.id);
}

/** The PIN-state reader: one's own (/users/moi/pin) or another user's (/users/:id/pin, the owner). */
export function lecteurPin(moi, cible, api) {
  return estSoi(moi, cible) ? () => api.monPin() : () => api.pinEtat(cible.id);
}

/** Whether `moi` is looking at their own row. */
export const surSoi = estSoi;

const RANG_ROLE = Object.freeze({ [ROLES.PROPRIETAIRE]: 0, [ROLES.MANAGER]: 1, [ROLES.CAISSIER]: 2 });

/** The owner first, then the managers, then the caissiers; each by name. */
export function trierEquipe(users) {
  return [...(users ?? [])].sort(
    (a, b) =>
      (RANG_ROLE[a.role] ?? 9) - (RANG_ROLE[b.role] ?? 9) ||
      sansAccents(a.nom).localeCompare(sansAccents(b.nom), "fr") ||
      sansAccents(a.prenom).localeCompare(sansAccents(b.prenom), "fr"),
  );
}

/** Text (name, e-mail, phone — accents and case ignored), role and state filters. */
export function filtrerEquipe(users, { q = "", role = FILTRE_TOUS, statut = FILTRE_TOUS } = {}) {
  const t = sansAccents(q).trim();
  const tel = String(q ?? "").replace(/\D/g, "");
  return (users ?? []).filter((u) => {
    if (role && role !== FILTRE_TOUS && u.role !== role) return false;
    if (statut === FILTRE_STATUT.ACTIFS && !u.actif) return false;
    if (statut === FILTRE_STATUT.INACTIFS && u.actif) return false;
    if (!t) return true;
    if (sansAccents(`${u.prenom ?? ""} ${u.nom ?? ""} ${u.nom ?? ""} ${u.prenom ?? ""}`).includes(t)) return true;
    if (sansAccents(u.email).includes(t)) return true;
    return Boolean(tel) && String(u.telephone ?? "").replace(/\D/g, "").includes(tel);
  });
}

/** The previous (-1) / next (+1) user of the visible list, or null at its ends (Ctrl+↑ / Ctrl+↓). */
export function voisin(liste, id, pas) {
  const i = (liste ?? []).findIndex((u) => u.id === id);
  if (i < 0) return null;
  return liste[i + pas]?.id ?? null;
}

/** The server's 422 field messages (`errors: [{ field: "body.<champ>", message }]`) as `{ champ: message }`. */
export function erreursChamps(err) {
  const out = {};
  for (const e of Array.isArray(err?.errors) ? err.errors : []) {
    const m = /^body\.(\w+)$/.exec(e?.field ?? "");
    if (m && !out[m[1]]) out[m[1]] = e.message;
  }
  return out;
}

/** The PIN state as words — never the PIN (XWP-65 answers no hash nor value). */
export function presentationPin(etat) {
  if (!etat) return { ton: "neutral", libelle: "—", detail: null };
  if (etat.verrouille) {
    return {
      ton: "danger",
      libelle: "PIN verrouillé",
      detail: etat.verrou_jusqu_a ? `Trop d'essais : verrouillé jusqu'à ${fmtTime(etat.verrou_jusqu_a)}. Le redéfinir le déverrouille.` : null,
    };
  }
  if (etat.defini) {
    return { ton: "success", libelle: "PIN défini", detail: etat.defini_le ? `Défini le ${fmtDate(etat.defini_le)}` : null };
  }
  return { ton: "neutral", libelle: "Aucun PIN", detail: "Sans PIN, l'utilisateur ne peut pas être choisi sur le poste d'équipe." };
}

/** « 18/10/2026 à 07:42 ». */
export function dateHeure(iso) {
  if (!iso) return null;
  return `${fmtDate(iso)} à ${fmtTime(iso)}`;
}

/** A seat line of `GET /users/sieges` (XWP-69) as words; the owner holds none (XO-16). */
export function presentationSiegeLigne(ligne, user) {
  if (user?.role === ROLES.PROPRIETAIRE) {
    return presentationMonSiege({ consomme_siege: false, etat: "SANS_SIEGE", message: MESSAGES_EQUIPE.sieges.proprietaire });
  }
  if (!ligne) return null;
  return presentationMonSiege({ ...ligne, consomme_siege: true });
}
